import { useCallback, useMemo, useState } from 'react';
import { makeStyles, tokens, Text, Spinner, TabList, Tab } from '@fluentui/react-components';
import { ShieldPersonRegular, OptionsRegular } from '@fluentui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useAppsInProject } from '@/hooks/useApps';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { PageSection } from '@/components/shared/PageSection';
import { ProjectOverviewHeader } from '@/components/shared/ProjectOverviewHeader';
import { ResourceErrorPage } from '@/components/shared/ResourceErrorPage';
import { ApiError } from '@/services/apiError';
import { Permissions } from '@/components/SettingsDialog/Permissions/Permissions';
import { ObservabilityTab } from '@/components/Settings/ObservabilityTab';
import { useCheckAccess, checkUserAccess, useIsProjectOwner } from '@/hooks/useUserPermissions';
import { useAuth } from '@/auth';
import { PermissionType, PROJECT_PERMISSIONS } from '@/types/permissions';

const useStyles = makeStyles({
  page: {
    // Outer padding + horizontal layout come from `ProjectLayout.mainContent`.
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  tabList: {
    // Pull the underline to the full width of the section so the active-tab
    // indicator reads as a divider between the tab strip and the panel below.
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  panel: {
    paddingTop: tokens.spacingVerticalL,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXXL,
  },
  errorText: {
    color: tokens.colorStatusDangerForeground1,
  },
});

export const ProjectSettingsPage = () => {
  const styles = useStyles();
  const { projectName, project, isLoading, notFound } = useCurrentProject();
  const { data: apps } = useAppsInProject(projectName ?? '');
  useDocumentTitle(['Settings', projectName]);
  const { account, getOttoToken } = useAuth();
  const queryClient = useQueryClient();
  const { data: myAccess, isLoading: accessLoading, isError } = useCheckAccess(projectName ?? '', undefined, null);
  const { data: isUserProjectAdmin, isLoading: isAdminAccessLoading } = useIsProjectOwner(projectName ?? '');
  const userCanEdit = useMemo(() => isUserProjectAdmin || myAccess?.role === 'Contributor', [isUserProjectAdmin, myAccess]);
  const [selectedTab, setSelectedTab] = useState<string>('permissions');
  const currentUserAccess = useMemo(() => {
    if (accessLoading || isAdminAccessLoading) return null;
    return isUserProjectAdmin
      ? { role: PermissionType.Owner, permissions: [{
          id: PermissionType.Owner,
          name: 'Owner',
          description: 'Full access to all project resources and permissions. Cannot manage application resources if not explicitly granted at the application level, although can delete application.',
      }] }
      : myAccess;
  }, [accessLoading, isAdminAccessLoading, isUserProjectAdmin, myAccess]);

  const resourceDetails = useMemo(() => ({ projectName: projectName ?? '' }), [projectName]);

  const checkAccess = useCallback(async (userId: string) => {
    try {
      return await checkUserAccess(queryClient, account?.tenantId ?? '', userId, projectName ?? '', undefined, getOttoToken);
    } catch (error) {
      console.error('Error checking access for user', userId, error);
      throw error;
    }
  }, [account?.tenantId, getOttoToken, projectName, queryClient]);

  if (isLoading) {
    return <Spinner size="medium" label="Loading project settings..." />;
  }

  if (notFound) {
    return <ResourceErrorPage resourceType="project" error={new ApiError('Not found', 404)} />;
  }

  return (
    <div className={styles.page}>
      {projectName && (
        <ProjectOverviewHeader
          name={projectName}
          project={project}
          appCount={apps?.length ?? 0}
        />
      )}

      <PageSection title="Settings">
        <TabList
          className={styles.tabList}
          selectedValue={selectedTab}
          onTabSelect={(_, data) => setSelectedTab(data.value as string)}
        >
          <Tab value="permissions" icon={<ShieldPersonRegular />}>
            User permissions
          </Tab>
          <Tab value="observability" icon={<OptionsRegular />}>
            Configuration
          </Tab>
        </TabList>
        <div className={styles.panel}>
          {selectedTab === 'permissions' && (
            <>
              {accessLoading || isAdminAccessLoading ? (
                <div className={styles.loadingContainer}>
                  <Spinner size="medium" label="Loading permissions..." />
                </div>
              ) : isError || !currentUserAccess ? (
                <Text className={styles.errorText}>
                  Failed to load permissions. Please try again.
                </Text>
              ) : (
                <Permissions
                  supportedPermissions={PROJECT_PERMISSIONS}
                  resourceDetails={resourceDetails}
                  myAccess={currentUserAccess}
                  checkAccess={checkAccess}
                  canEdit={userCanEdit}
                />
              )}
            </>
          )}
          {selectedTab === 'observability' && projectName && (
            accessLoading || isAdminAccessLoading ? (
              <div className={styles.loadingContainer}>
                <Spinner size="medium" label="Loading observability settings..." />
              </div>
            ) : isError || !currentUserAccess ? (
              <Text className={styles.errorText}>
                Failed to load Application Insights configuration. Please try again.
              </Text>
            ) : (
              <ObservabilityTab projectName={projectName} canEdit={userCanEdit} />
            )
          )}
        </div>
      </PageSection>
    </div>
  );
};
